import { PageAtmosphere } from '@/types/atmosphere';
import { clearAuth, getAccessToken, getAuthStorage, getValidAccessToken, isJwtExpired } from './authStorage';
import { log } from './logger';
import { publicRuntimeConfig } from './runtimeConfig';
import type { ProjectPlanDependencyType, ProjectPlanSourceFormat, ProjectPlanV1 } from './projectPlanExchange';
import type { SiteThemeId, SiteThemes, ThemeOverrides } from './siteTheme';
import type { BackgroundConfig, SiteBackgroundSettings } from './siteBackgroundSettings';
import type { ProjectDirection } from './projectDirections';
import type { NewsContent } from '@/types/blocks';

export type { ProjectPlanDependencyType, ProjectPlanSourceFormat, ProjectPlanV1 } from './projectPlanExchange';

const DEBUG = publicRuntimeConfig.debug;

export const API_URL = publicRuntimeConfig.apiUrl;
export const WS_BASE = publicRuntimeConfig.websocketUrl;
export const REALTIME_MODE = publicRuntimeConfig.realtimeMode;

if (DEBUG) {
  log('debug', 'api', 'Public runtime config loaded', {
    apiUrl: API_URL,
    websocketUrl: WS_BASE,
    colyseusUrl: publicRuntimeConfig.colyseusUrl,
    realtimeMode: REALTIME_MODE,
  });
}

export class ApiError extends Error {
  status?: number;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Типы для API
export interface UserRole {
  id: number;
  name: string;
  priority?: number;
  color?: string | null;
  permissions?: string[];
}

export interface GamePresence {
  game_id: string;
  game_title: string;
  state: string;
  state_label?: string | null;
  label: string;
  detail?: string | null;
  map_id?: string | null;
  map_title?: string | null;
  match_id?: number | null;
  lobby_id?: number | null;
  url?: string | null;
  updated_at?: string | null;
}

export interface User {
  id: number;
  username: string;
  // Комментарий на русском: публичный хэндл для URL вида /{nickname} и отображения @nickname.
  // Если отсутствует (старые аккаунты/старая БД) — используем username.
  nickname?: string | null;
  nickname_changed_at?: string | null;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  display_name?: string | null;
  // Комментарий на русском: role — главная роль (по приоритету), roles — все роли пользователя.
  role?: UserRole | null;
  roles?: UserRole[];
  avatar?: string;
  avatar_url?: string;
  avatar_display_url?: string;
  bio?: string;
  phone?: string;
  position?: string;
  organization?: string;
  website?: string;
  github_username?: string;
  telegram_username?: string;
  // Публичное время работы (по желанию сотрудника)
  work_time?: string | null;
  is_profile_public?: boolean;
  email_notifications?: boolean;
  show_email?: boolean;
  // 0=скрыт, 1=открыт, 2=открыт (расширенный)
  profile_visibility?: 0 | 1 | 2;
  // Комментарий на русском: true, если профиль скрыт и замаскирован для этого viewer.
  is_profile_masked?: boolean;
  // Резюме/научный профиль (структура JSON, поля могут расширяться без миграций)
  resume_data?: any;
  resume_data_revision?: string;
  // Портфолио/навыки (структура JSON)
  portfolio_data?: any;
  // Пользовательские настройки интерфейса (project_ui_mode и т.д.)
  preferences?: Record<string, any>;
  is_active: boolean;
  is_staff?: boolean;
  is_superuser: boolean;
  game_presence?: GamePresence | null;
  created_at: string;
  permissions?: string[];
  has_local_password?: boolean;
  auth_identities?: Array<{
    provider: 'google' | 'vk' | 'telegram' | 'max';
    linked_at: string;
    username?: string | null;
    email?: string | null;
  }>;

  // Статистика посещений (админка / трекинг визитов)
  visits_today?: number;
  visits_month?: number;
  visits_total?: number;
  visits_last_at?: string | null;
  last_login?: string | null;
  can_edit?: boolean;
  projects_created?: number;
  projects?: Array<Pick<Project, 'id' | 'name' | 'created_at' | 'updated_at' | 'views_count' | 'likes_count' | 'comments_count'>>;
  admin_stats?: {
    projects_created?: number;
    storage_bytes?: number | null;
    news_created: number;
    comments_created: number;
  };
}

// Комментарий на русском: безопасный предпросмотр профиля (для /users/[id] и кликов из мессенджера/сотрудников).
export type UserPreview = Partial<User> & {
  id: number;
  username: string;
  friendship_status?: string | null;
  is_online?: boolean;
  last_seen?: string | null;
};

export type AppointmentMeetingMode = 'offline' | 'online';
export type AppointmentStatus = 'booked' | 'canceled' | 'completed' | 'no_show';
export type AppointmentActorRole = 'owner' | 'delegate' | 'admin' | 'visitor';

export interface AppointmentChangeSource {
  changed_by?: number | null;
  changed_role?: AppointmentActorRole | null;
}

export interface AppointmentOwner {
  id: number;
  username: string;
  nickname?: string | null;
  display_name: string;
  avatar_url?: string | null;
  position?: string | null;
  organization?: string | null;
}

export interface AppointmentSchedule {
  owner_user_id?: number;
  is_enabled?: boolean;
  timezone: string;
  slot_duration_minutes: number;
  buffer_minutes?: number;
  booking_horizon_days: number;
  min_notice_minutes: number;
  default_meeting_mode: AppointmentMeetingMode;
  offline_location?: string | null;
  online_url?: string | null;
}

export interface AppointmentSlot {
  starts_at: string;
  ends_at: string;
  state: 'available' | 'occupied';
  meeting_mode: AppointmentMeetingMode;
}

export interface AppointmentAvailabilityRule extends AppointmentChangeSource {
  id?: number;
  weekday: number;
  start_time: string;
  end_time: string;
  meeting_mode?: AppointmentMeetingMode | null;
  location_text?: string | null;
  online_url?: string | null;
}

export interface AppointmentAvailabilityException extends AppointmentChangeSource {
  id?: number;
  local_date: string;
  start_time?: string | null;
  end_time?: string | null;
  exception_type: 'closed' | 'open';
  meeting_mode?: AppointmentMeetingMode | null;
  location_text?: string | null;
  online_url?: string | null;
  reason?: string | null;
}

export interface AppointmentEvent {
  actor_role?: AppointmentActorRole | null;
  id: number;
  event_type: 'created' | 'updated' | 'rescheduled' | 'canceled' | 'completed' | 'no_show';
  actor_kind: 'user' | 'guest' | 'system';
  actor_user_id?: number | null;
  actor_name?: string | null;
  payload?: Record<string, unknown> | null;
  created_at: string;
}

export type AppointmentDelegate = AppointmentOwner;

export interface AppointmentBooking {
  visitor_user_id?: number | null;
  visitor_handle?: string | null;
  restriction?: AppointmentVisitorRestriction | null;
  id: number;
  owner_user_id: number;
  owner?: AppointmentOwner;
  topic: string;
  starts_at: string;
  ends_at: string;
  meeting_mode: AppointmentMeetingMode;
  location_text?: string | null;
  online_url?: string | null;
  status: AppointmentStatus;
  timezone: string;
  visitor_name?: string;
  visitor_email?: string;
  canceled_at?: string | null;
  cancel_reason?: string | null;
  events?: AppointmentEvent[];
}

export interface AppointmentManagedSchedule {
  owner: AppointmentOwner;
  has_schedule: boolean;
  is_enabled: boolean;
  timezone: string;
  slot_duration_minutes: number;
  upcoming_count: number;
  access: 'owner' | 'delegate' | 'admin';
}

export interface AppointmentManagementPayload {
  schedule: AppointmentSchedule;
  rules: AppointmentAvailabilityRule[];
  exceptions: AppointmentAvailabilityException[];
  delegates: AppointmentDelegate[];
  teaching_rules?: AppointmentTeachingRule[];
  teaching_conflict_count?: number;
}

export interface AccountMergePreview {
  eligible: boolean;
  source_account: {
    username: string;
    display_name: string;
    email?: string | null;
    created_at?: string | null;
  };
  projects: {
    owned: number;
    member_of: number;
  };
  providers: string[];
  will_transfer_local_credentials: boolean;
  blockers: Array<{
    code: string;
    message: string;
    count?: number;
    providers?: string[];
  }>;
}

export interface AccountMergeRequest {
  merge_token: string;
  expires_in?: number;
  expires_at?: string;
  preview: AccountMergePreview;
}

export interface AppointmentTeachingRule extends AppointmentChangeSource {
  id: number;
  term_start: string;
  term_end: string;
  weekday: number;
  title: string;
  start_time: string;
  end_time: string;
  break_before_minutes: number;
  break_after_minutes: number;
  is_enabled: boolean;
}

export type AppointmentTeachingInput = Omit<AppointmentTeachingRule, 'id' | 'changed_by' | 'changed_role'>;

export interface AppointmentVisitorRestriction {
  expires_at: string;
  reason?: string | null;
}

export interface AppointmentCalendarPayload extends AppointmentManagementPayload {
  from: string;
  to: string;
  bookings: AppointmentBooking[];
}

export interface AppointmentCreatePayload {
  owner_id: number;
  starts_at: string;
  topic: string;
  name?: string;
  email?: string;
  verification_token?: string;
  verification_code?: string;
  idempotency_key: string;
}

export type AppointmentLifecyclePayload =
  | { action: 'cancel'; cancel_reason?: string }
  | { action: 'reschedule'; starts_at: string };

export type AppointmentManagerLifecyclePayload = AppointmentLifecyclePayload | { action: 'completed' | 'no_show' } | { action: 'update'; topic: string; meeting_mode: AppointmentMeetingMode; location_text: string | null; online_url: string | null };

// Организации (университеты, институты, кафедры, лаборатории, школы, колледжи, гимназии)
export type OrganizationType = 'university' | 'college' | 'institute' | 'department' | 'laboratory' | 'school' | 'gymnasium' | 'organization';

export interface Organization {
  id: number;
  name: string;
  short_name?: string | null;
  type: OrganizationType;
  type_label?: string;
  parent_id?: number | null;
  parent_name?: string | null;
  parent_short_name?: string | null;
  description?: string | null;
  cover_url?: string | null;
  website?: string | null;
  address?: string | null;
  color?: string;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
  children_count?: number;
  projects_count?: number;
  children?: Organization[];
  created_at?: string;
  updated_at?: string;
}

export interface OrganizationTypeOption {
  value: OrganizationType;
  label: string;
}

// Проекты
export interface Project {
  id: number;
  name: string;
  description?: string;
  directions?: ProjectDirection[];
  is_public: boolean;
  organization_id?: number | null;
  organization?: Organization | null;
  // Метрики (как у новостей)
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
  user_reaction?: string | null;
  // Комментарий на русском: количество научных статей (meta.articles), вычисляется на бэкенде.
  articles_count?: number;
  // Модерация (для проектов лаборатории)
  moderation_status?: 'draft' | 'pending' | 'approved' | 'rejected' | string;
  moderated_at?: string | null;
  moderated_by?: number | null;
  status?: 'idea' | 'active' | 'paused' | 'completed' | 'archived' | string;
  progress?: number; // 0..100
  display_rank?: number | null;
  can_edit_basics?: boolean;
  cover_url?: string | null;
  scientific_field?: string | null;
  keywords?: string | null;
  start_date?: string | null; // YYYY-MM-DD
  end_date?: string | null; // YYYY-MM-DD
  materials?: string | null;
  meta?: {
    articles?: Array<{ title?: string; url?: string }>;
    attributes?: Array<{ key?: string; value?: string }>;
    milestones?: Array<{ title?: string; due_date?: string; status?: string; description?: string }>;
    materials_blocks?: any;
  } | null;
  // Связанные новости из таблицы project_news_relations (истина для internal /news/{slug})
  linked_news?: Array<{ id: number; slug: string; title: string; url: string; published_at?: string | null }>;
  owner: number;
  owner_can_view_info?: boolean;
  owner_username?: string;
  owner_display?: string;
  owner_role?: { name: string; color?: string | null; priority?: number | null } | null;
  created_at: string;
  updated_at: string;
  members_count?: number;
  is_member?: boolean;
  my_role?: string;
  // Комментарий на русском: новые уровни доступа к проекту (1..5) + должность в проекте.
  my_access_level?: number;
  my_position?: string | null;
  // Комментарий на русском: статус моей заявки на вступление (если я ещё не участник).
  my_join_request_id?: number;
  my_join_request_status?: string;
  // Автор целевой публичной витрины; не роль посетителя и не список участников.
  profile_user_id?: number;
  profile_role?: 'owner';
}

export interface ProjectMember {
  id: number;
  user_id: number;
  project: number;
  username: string;
  user_display: string;
  role: string;
  access_level: number;
  position?: string | null;
  joined_at: string;
  nickname?: string | null;
  avatar_url?: string | null;
  avatar_fx_color?: string | null;
  avatar_fx_thickness?: number | null;
  avatar_fx_glow?: number | null;
  last_project_opened_at?: string | null;
  last_project_worked_at?: string | null;
}

export interface ProjectCollaborator {
  user_id: number;
  username: string;
  display_name: string;
  editing_block_uid: string | null;
  last_seen: string;
}

export interface ProjectCollaborationState {
  server_time: string;
  latest_event_id: number;
  participants: ProjectCollaborator[];
}

export interface ProjectInvite {
  id: number;
  project_id: number;
  project_name: string;
  invited_by: number;
  invited_by_username: string;
  invited_by_display: string;
  message?: string | null;
  created_at: string;
}

export interface ProjectInviteAdminRow {
  id: number;
  project_id: number;
  invited_user_id: number;
  invited_username: string;
  invited_display: string;
  invited_by: number;
  invited_by_username: string;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | string;
  message?: string | null;
  created_at: string;
  responded_at?: string | null;
}

export interface ProjectJoinRequestOwnerRow {
  id: number;
  project_id: number;
  project_name: string;
  user_id: number;
  message?: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | string;
  created_at: string;
  responded_at?: string | null;
  responded_by?: number | null;
  decision_comment?: string | null;
  sender: Partial<User> & { id: number; username: string };
}

export interface ProjectJoinRequestMyRow {
  id: number;
  project_id: number;
  project_name: string;
  message?: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | string;
  created_at: string;
  responded_at?: string | null;
  responded_by?: number | null;
  decision_comment?: string | null;
  owner_id: number;
  owner_username: string;
  owner_display: string;
  owner_avatar_url?: string | null;
}

export interface ProjectMaterialsBlocksProposalRow {
  id: number;
  project_id: number;
  author_id: number;
  status: 'pending' | 'approved' | 'declined' | string;
  created_at: string;
  content?: any;
  author: Partial<User> & { id: number; username: string };
}

// ============================================================
// Блоки проекта (кубики) — новая архитектура
// ============================================================

// Контентные типы блоков
export type ContentBlockType = 'note' | 'section' | 'reference' | 'table' | 'requirement' | 'task' | 'decision' | 'experiment' | 'result' | 'risk' | 'asset' | 'milestone' | 'project' | 'custom';
// Управляющие типы блоков (Blueprint)
export type ControlBlockType = 'entry' | 'if_branch' | 'merge' | 'sequence' | 'switch';
// Типы данных/выражений (Blueprint)
export type DataBlockType = 'var_get' | 'var_set' | 'const' | 'compare' | 'math';
// Все типы блоков
export type ProjectBlockType = ContentBlockType | ControlBlockType | DataBlockType;
export type ProjectBlockStatus = 'draft' | 'proposed' | 'published' | 'rejected' | 'archived';
// Статус ревью блока
export type ReviewState = 'none' | 'in_review' | 'changes_requested' | 'approved';
export type ProjectEdgeType = 'relates_to' | 'depends_on' | 'blocks' | 'derived_from' | 'validates' | 'contradicts' | 'duplicates' | 'supersedes' | 'implements' | 'custom';
// Blueprint UE5: типы данных для Data Flow связей
// Расширенные типы для научных блоков: dataset, figure, model, protocol
export type DataFlowType = 'any' | 'number' | 'text' | 'file' | 'reference' | 'result' | 'decision' | 'bool' | 'dataset' | 'figure' | 'model' | 'protocol';
// Типы переменных проекта
export type ProjectVariableType = 'number' | 'text' | 'bool' | 'date' | 'array' | 'object';

// === Типы для файлового менеджера проектов ===
export interface ProjectFileItem {
  id: string;
  name: string;
  filename: string;
  path: string;
  folder: string;
  url: string;
  size: number;
  type: string;
  uploaded_at: string;
  dimensions?: { width: number; height: number };
}

export interface ProjectFolderItem {
  name: string;
  path: string;
  items_count: number;
  folders_count: number;
  size: number;
  modified_at: string;
  is_empty?: boolean;
}

export interface ProjectBreadcrumb {
  label: string;
  path: string;
}

export interface ProjectStorageInfo {
  used: number;
  max: number;
  remaining: number;
  used_percent?: number;
  folders_count: number;
  max_folders: number;
  max_file_size?: number;
}

export interface ProjectFilesListResponse {
  folders: ProjectFolderItem[];
  files: ProjectFileItem[];
  total: number;
  current_folder: string;
  parent_folder: string | null;
  breadcrumbs: ProjectBreadcrumb[];
  storage_info: ProjectStorageInfo;
}

export interface ProjectFileUploadResponse {
  success: boolean;
  file: ProjectFileItem;
  storage_info: { used: number; max: number };
}

export interface ProjectFolderCreateResponse {
  success: boolean;
  folder: ProjectFolderItem;
  storage_info: { folders_count: number; max_folders: number };
}

export interface ProjectBlock {
  id: number;
  uid: string;
  type: ProjectBlockType;
  type_version?: string;
  title: string;
  content?: string | null;
  status: ProjectBlockStatus;
  // Статус ревью (для Review workflow)
  review_state?: ReviewState;
  reviewer_id?: number | null;
  reviewed_at?: string | null;
  importance: number;          // 0=P0 критично, 3=P3 низкий
  implementation_level: number; // 0=I0 не начато, 5=I5 полностью
  content_hash?: string | null;
  author_id: number;
  author_username?: string;
  author_display?: string;
  position: number;
  tags?: string[] | null;
  meta?: Record<string, any> | null;
  // Blueprint: типы данных для пинов блока
  input_data_types?: DataFlowType[] | null;
  output_data_types?: DataFlowType[] | null;
  // Выражение для управляющих блоков (If, Compare, Math)
  expression?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
}

export interface ProjectDiffBlockSnapshot {
  uid: string;
  type: ProjectBlockType;
  title: string;
  content?: string | null;
  content_hash?: string | null;
  content_truncated?: boolean;
  status: ProjectBlockStatus;
  importance: number;
  implementation_level: number;
  updated_at: string;
}

export interface ProjectDiffBlockChange {
  uid: string;
  change_type: 'added' | 'updated' | 'deleted';
  before: ProjectDiffBlockSnapshot | null;
  after: ProjectDiffBlockSnapshot | null;
}

export interface ProjectDiffStateSummary {
  blocks: number;
  edges: number;
  block_types: Partial<Record<ProjectBlockType, number>>;
}

export interface ProjectDiffEdge {
  from_uid: string | null;
  to_uid: string | null;
  type: string | null;
  label?: string | null;
  edge_id?: number | null;
  from_title?: string | null;
  to_title?: string | null;
}

export interface ProjectDiffResponse {
  t1: string;
  t2: string;
  events_count: number;
  blocks_added: string[];
  blocks_updated: string[];
  blocks_deleted: string[];
  edges_added: ProjectDiffEdge[];
  edges_deleted: ProjectDiffEdge[];
  state_counts?: { t1: ProjectDiffStateSummary; t2: ProjectDiffStateSummary };
  block_changes?: ProjectDiffBlockChange[];
  block_changes_truncated?: boolean;
  block_changes_limit?: number;
}

// Переменная проекта
export interface ProjectVariable {
  id: number;
  project_id: number;
  name: string;
  var_type: ProjectVariableType;
  default_value?: string | null;
  current_value?: string | null;
  description?: string | null;
  is_readonly: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Ревью блока
export interface BlockReview {
  id: number;
  project_id: number;
  scope: 'node' | 'pipeline' | 'build';
  target_id: string;
  target_version_id?: number | null;
  title?: string | null;
  status: 'open' | 'approved' | 'changes_requested' | 'closed';
  requester_id: number;
  requester_username?: string;
  requester_display?: string;
  assignee_id?: number | null;
  assignee_username?: string;
  assignee_display?: string;
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
}

// Комментарий к ревью
export interface ReviewComment {
  id: number;
  review_id: number;
  block_id?: number | null;
  version_id?: number | null;
  parent_id?: number | null;
  author_id: number;
  author_username?: string;
  author_display?: string;
  comment_type: 'comment' | 'suggestion' | 'issue' | 'approval' | 'rejection';
  content: string;
  suggestion_diff?: Record<string, any> | null;
  line_start?: number | null;
  line_end?: number | null;
  resolved: boolean;
  resolved_by?: number | null;
  resolved_at?: string | null;
  created_at: string;
}

// Шаблон Overview
export interface OverviewTemplate {
  id: number;
  template_id: string;
  title: string;
  description?: string | null;
  category: 'content' | 'control' | 'layout' | 'special';
  compatibility: 'presentation' | 'document' | 'both';
  payload_schema: Record<string, any>;
  default_layout?: Record<string, any> | null;
  icon?: string | null;
  is_builtin: boolean;
}

// Theme Pack
export interface ThemePack {
  id: number;
  theme_id: string;
  title: string;
  description?: string | null;
  tokens: {
    colors: Record<string, string>;
    typography: Record<string, string>;
    spacing: Record<string, string>;
    components: Record<string, string>;
  };
  is_builtin: boolean;
}

// Определение типа блока (Universal Block API)
export interface BlockTypeDefinition {
  id: number;
  type_id: string;
  title: string;
  description?: string | null;
  category: 'content' | 'control' | 'data' | 'custom';
  version: string;
  pins_schema: Array<{
    name: string;
    kind: 'exec' | 'data';
    direction: 'in' | 'out';
    data_type?: DataFlowType;
    multiplicity?: 'one' | 'many';
    label?: string;
  }>;
  payload_schema: Record<string, any>;
  default_payload?: Record<string, any> | null;
  template_defaults?: Record<string, any> | null;
  icon?: string | null;
  color?: string | null;
  is_builtin: boolean;
}

// Overview AST (результат компиляции)
export interface OverviewAST {
  project_id: number;
  at: string;
  graph_hash: string;
  errors: Array<{ node_id?: string; edge_id?: number; message: string; code: string }>;
  warnings: Array<{ node_id?: string; edge_id?: number; message: string; code: string }>;
  document: {
    title: string;
    pipelines: OverviewPipeline[];
  };
}

export interface OverviewPipeline {
  entry_node_id: number;
  entry_uid: string;
  title: string;
  flow: OverviewFlowItem[];
}

export type OverviewFlowItem = OverviewSlide | OverviewBranch;

export interface OverviewSlide {
  type: 'slide';
  node_id: number;
  node_uid: string;
  template: string;
  payload: Record<string, any>;
}

export interface OverviewBranch {
  type: 'branch';
  node_id: number;
  node_uid: string;
  condition: {
    expr: string;
    resolved: boolean;
    value: boolean | null;
  };
  then: OverviewFlowItem[];
  else: OverviewFlowItem[];
  merge_node_id?: number | null;
}

export interface ProjectEdge {
  id: number;
  from_block_id: number;
  from_uid: string;
  from_title?: string;
  from_type?: ProjectBlockType;
  to_block_id: number;
  to_uid: string;
  to_title?: string;
  to_type?: ProjectBlockType;
  relation_type: ProjectEdgeType;
  // Blueprint: тип передаваемых данных
  data_type?: DataFlowType;
  label?: string | null;
  weight: number;
  meta?: Record<string, unknown> | null;
  author_id: number;
  created_at: string;
}

export type ProjectScheduleDependencyType = ProjectPlanDependencyType;

export function formatProjectEdgeSchedule(edge: ProjectEdge): string | null {
  if (edge.relation_type !== 'depends_on') return null;
  const meta = edge.meta && typeof edge.meta === 'object' ? edge.meta : {};
  const rawType = String(meta.schedule_type || 'FS').toUpperCase();
  const type: ProjectScheduleDependencyType = ['FS', 'SS', 'FF', 'SF'].includes(rawType)
    ? rawType as ProjectScheduleDependencyType
    : 'FS';
  const rawLag = Number(meta.lag_minutes ?? 0);
  const lag = Number.isFinite(rawLag) ? rawLag : 0;
  const sign = lag > 0 ? '+' : lag < 0 ? '−' : '';
  const minutes = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Math.abs(lag));
  return `${type} · ${sign}${minutes} мин`;
}

export type ProjectPlanImportResolution = 'file' | 'lisad';

export interface ProjectPlanImportSummary {
  added: number;
  updated: number;
  unchanged: number;
  conflicts: number;
}

export interface ProjectPlanImportChange {
  key: string;
  kind: 'stage' | 'task' | 'dependency';
  externalId: string;
  title: string;
  action: 'added' | 'updated' | 'unchanged' | 'conflict';
}

export interface ProjectPlanImportDiagnostic {
  code?: string;
  message: string;
  severity?: 'error' | 'warning';
  path?: string;
  location?: string;
}

export interface ProjectPlanImportConflict {
  key: string;
  kind: 'stage' | 'task' | 'dependency';
  externalId: string;
  title: string;
  current: Record<string, unknown> | null;
  incoming: Record<string, unknown>;
}

export interface ProjectPlanResponse {
  success: true;
  plan: ProjectPlanV1;
  revision: string;
  warnings: string[];
}

export interface ProjectPlanImportPreviewResponse {
  success: true;
  mode: 'preview';
  baseRevision: string;
  planHash: string;
  summary: ProjectPlanImportSummary;
  changes: ProjectPlanImportChange[];
  warnings: string[];
  diagnostics: ProjectPlanImportDiagnostic[];
  conflicts: ProjectPlanImportConflict[];
}

export interface ProjectPlanImportCommitResponse {
  success: true;
  mode: 'commit';
  revision: string;
  summary: ProjectPlanImportSummary;
  warnings: string[];
}

export type ProjectPlanImportRequest =
  | {
      mode: 'preview';
      plan: ProjectPlanV1;
      sourceFormat?: ProjectPlanSourceFormat;
      resourceMappings?: Record<string, number>;
    }
  | {
      mode: 'commit';
      plan: ProjectPlanV1;
      baseRevision: string;
      planHash: string;
      resolutions: Record<string, ProjectPlanImportResolution>;
      sourceFormat?: ProjectPlanSourceFormat;
      resourceMappings?: Record<string, number>;
      confirmUnboundPlan?: true;
    };

export interface GraphValidationIssue {
  code: string;
  message: string;
  node_uid?: string;
  edge_id?: number;
}

export interface GraphValidationResult {
  errors: GraphValidationIssue[];
  warnings: GraphValidationIssue[];
  draft_uids: string[];
}

export interface ProjectEvent {
  id: number;
  time: string;
  actor_id?: number | null;
  actor_username?: string | null;
  actor_display?: string | null;
  event_type: string;
  target_type?: string | null;
  target_id?: string | null;
}

export interface ProjectBlockApproval {
  id: number;
  block_id: number;
  block_uid: string;
  block_title: string;
  block_type: ProjectBlockType;
  requester_id: number;
  requester_username: string;
  requester_display: string;
  request_type: 'publish' | 'edit' | 'delete';
  message?: string | null;
  created_at: string;
}

export interface ProjectBlockVersion {
  id: number;
  version_num: number;
  is_full: boolean;
  base_version_id?: number | null;
  content_hash?: string | null;
  title: string;
  status: ProjectBlockStatus | string;
  importance?: number | null;
  implementation_level?: number | null;
  author_id: number;
  author_username?: string;
  author_display?: string;
  change_summary?: string | null;
  created_at: string;
  content?: string | null; // может приходить только при with_content=1
}

export interface BlockTypeInfo {
  id: ProjectBlockType;
  label: string;
  icon: string;
  color: string;
}

export interface EdgeTypeInfo {
  id: ProjectEdgeType;
  label: string;
  directed: boolean;
}

export interface ProjectDocs {
  project_id: number;
  project_name: string;
  blocks_count: number;
  generated_at: string;
  markdown: string;
}

export type ProjectLinkRelation = 'base' | 'part';
export interface ProjectPresentationRelation {
  source_project_id: number;
  source_block_uid: string;
  direction: 'outgoing' | 'incoming';
  relation: ProjectLinkRelation;
  status: string;
  block_status: ProjectBlockStatus;
  can_edit: boolean;
  can_delete: boolean;
  project: { id: number; name: string; description: string; cover_url?: string | null } | null;
}

export interface ProjectPresentation {
  project_id: number;
  name: string;
  updated_at: string;
  materials: NewsContent;
  relations: ProjectPresentationRelation[];
  can_add_links: boolean;
  can_approve_links: boolean;
}
export type ProjectLinkRequestStatus = 'pending' | 'approved' | 'declined' | 'revoked' | string;

export interface ProjectLinkRequestOwnerRow {
  id: number;
  source_project_id: number;
  source_project_name: string;
  target_project_id: number;
  relation: ProjectLinkRelation;
  block_uid: string;
  requester_id: number;
  requester_username: string;
  requester_display: string;
  status: ProjectLinkRequestStatus;
  created_at: string;
}

export interface NewsItem {
  id: number;
  title: string;
  teaser: string;
  content: string;
  content_blocks?: {
    version: string;
    blocks: any[];
  };
  slug: string;
  status: 'draft' | 'published' | 'archived' | 'review' | 'scheduled';
  published_at?: string;
  created_at?: string;
  page_atmosphere?: PageAtmosphere | string | null; // Настройки атмосферы страницы (может быть JSON строкой)
  updated_at?: string;
  // В PHP-версии бэкенда author может приходить null
  author: User | null;
  category?: {
    id: number;
    name: string;
    slug: string;
    color: string;
    description?: string;
  };
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  views_count: number;
  comments_count: number;
  reading_time?: number; // Время чтения в минутах
  is_published?: boolean; // Флаг публикации
  user_reaction?: string; // Текущая реакция пользователя на новость
  likes_count: number; // Количество лайков
  cover_url?: string;
  meta_title?: string;
  meta_description?: string;
  // Проекты, связанные с новостью (из project_news_relations)
  projects?: Array<{ id: number; name: string; status?: string; progress?: number; url?: string }>;
  // Для админки
  project_ids?: number[];
}

// Комментарий на русском: в разных бэкендах JSON-поля могут приходить строкой.
const parseMaybeJson = <T,>(value: any): T | any => {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  if (!s) return value;
  try {
    return JSON.parse(s) as T;
  } catch {
    return value;
  }
};

const normalizeNewsItem = (raw: any): NewsItem => {
  if (!raw || typeof raw !== 'object') return raw as NewsItem;
  const next: any = { ...raw };
  next.content_blocks = parseMaybeJson(next.content_blocks);
  next.page_atmosphere = parseMaybeJson(next.page_atmosphere);
  return next as NewsItem;
};

// Справочники новостей
export interface Category {
  id: number;
  name: string;
  slug: string;
  color?: string;
  description?: string;
}

export interface Tag {
  id: number;
  name: string;
  slug: string;
}

export interface Comment {
  id: number;
  content: string;
  author: User;
  block_id?: string | null;
  parent_id?: number;
  upvotes: number;
  downvotes: number;
  status: 'visible' | 'hidden' | 'pending' | 'rejected';
  // Комментарий на русском: реакция текущего пользователя (если авторизован).
  user_reaction?: 'like' | 'heart' | null;
  created_at: string;
  edited_at?: string;
  // Бэкенд возвращает replies; поддержим оба поля для совместимости
  replies?: Comment[];
  children?: Comment[];
}

export interface NewsSearchItem {
  id: number;
  slug: string;
  title: string;
  teaser?: string;
  published_at?: string | null;
}

export interface UserDirectoryItem {
  id: number;
  username: string;
  display_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
}

export interface SiteSettings {
  site_background?: SiteBackgroundSettings;
  site_themes?: SiteThemes;
  id: number;
  contact_address: string;
  contact_email: string;
  contact_phone: string;
  directions: string[];
  social_links: {
    github?: string;
    telegram?: string;
    vk?: string;
    youtube?: string;
    instagram?: string;
    twitter?: string;
  };
  // Содержимое главной страницы
  main_title: string;
  main_subtitle: string;
  main_about_title: string;
  main_about_text: string;
  // Содержимое страницы О лаборатории
  about_lab_title: string;
  about_lab_subtitle: string;
  about_lab_description: string;
  about_lab_mission: string;
  about_lab_vision: string;
  about_lab_values: string;
  // Настройки виджетов и визуализации
  widget_opacity: number;
  widget_border_radius: number;
  widget_shadow_intensity: number;
  widget_primary_color: string;
  widget_secondary_color: string;
  widget_background_blur: boolean;
  widget_hover_effects: boolean;
  widget_animation_duration: number;
  // Настройки новостных карточек
  news_card_layout: 'default' | 'compact' | 'featured';
  news_card_show_author: boolean;
  news_card_show_date: boolean;
  news_card_show_tags: boolean;
  // Эффекты частиц внутри виджетов
  widget_particles_enabled: boolean;
  widget_particles_count: number;
  widget_particles_color: string;
  widget_particles_speed: number;
  widget_particles_size: number;
  // Hover-эффекты
  widget_hover_opacity: number;
  widget_hover_scale: number;
  // Комментарий на русском: emoji packs для мессенджера.
  messenger_emoji_pack_ids?: string[];
  messenger_system_emoji_pack_id?: string;
  created_at: string;
  updated_at: string;
  updated_by?: User;
}

export interface UpdatesSnapshot {
  server_time?: string;
  public?: {
    news?: number | null;
    projects?: number | null;
    vacancies?: number | null;
    settings?: number | null;
  };
  user?: {
    id?: number;
    messages?: {
      direct_last_id?: number | null;
      room_last_id?: number | null;
      support_last_id?: number | null;
    };
  };
}

export interface RolePermission {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
}

export interface AdminRole {
  id: number;
  name: string;
  description?: string;
  color: string;
  priority: number;
  users_count: number;
}

export interface AdminUsersStats {
  total: number;
  active: number;
  blocked: number;
}

export interface AdminServerStats {
  generated_at: string;
  disk: null | { path?: string; total_bytes: number; used_bytes: number; free_bytes: number; used_percent: number };
  ram: null | { total_bytes: number; used_bytes: number; available_bytes: number; used_percent: number };
  cpu: null | { cores: number; percent?: number | null; loadavg_1?: number | null; loadavg_5?: number | null; loadavg_15?: number | null; model?: string | null; freq_mhz?: number | null; max_freq_mhz?: number | null };
  network?: null | { bytes_sent: number; bytes_recv: number; sent_per_sec?: number | null; recv_per_sec?: number | null; requests_per_sec?: number | null; link_mbps?: number | null };
  errors?: Record<string, string>;
}

export interface PaginatedResponse<T> {
  results: T[];
  count?: number;
  next?: string | null;
  previous?: string | null;
  [key: string]: unknown;
}

type RawListResponse<T> = T[] | PaginatedResponse<T> | { data: T[] };

export interface NormalizedListResponse<T> {
  items: T[];
  count?: number;
  next?: string | null;
  previous?: string | null;
  raw: RawListResponse<T> | null;
}

function normalizeListResponse<T>(response: RawListResponse<T> | null | undefined): NormalizedListResponse<T> {
  if (!response) {
    return { items: [], raw: null };
  }

  if (Array.isArray(response)) {
    return {
      items: response,
      count: response.length,
      next: null,
      previous: null,
      raw: response,
    };
  }

  if ('results' in response && Array.isArray(response.results)) {
    return {
      items: response.results,
      count: typeof response.count === 'number' ? response.count : response.results.length,
      next: (response.next ?? null) as string | null,
      previous: (response.previous ?? null) as string | null,
      raw: response,
    };
  }

  if ('data' in response && Array.isArray(response.data)) {
    return {
      items: response.data,
      count: response.data.length,
      next: null,
      previous: null,
      raw: response,
    };
  }

  if ('items' in (response as Record<string, unknown>) && Array.isArray((response as any).items)) {
    const items = (response as any).items as T[];
    return {
      items,
      count: typeof (response as any).count === 'number' ? (response as any).count : items.length,
      next: (response as any).next ?? null,
      previous: (response as any).previous ?? null,
      raw: response,
    };
  }

  return { items: [], raw: response };
}

// API функции
export class ApiClient {
  private captureAuthSession() {
    const storage = getAuthStorage();
    return {
      storage,
      access: storage?.getItem('access') ?? null,
      refresh: storage?.getItem('refresh') ?? null,
    };
  }

  private isSameAuthSession(
    left: ReturnType<ApiClient['captureAuthSession']>,
    right: ReturnType<ApiClient['captureAuthSession']>
  ) {
    return left.storage === right.storage && left.access === right.access && left.refresh === right.refresh;
  }

  private isCurrentAuthSession(session: ReturnType<ApiClient['captureAuthSession']>) {
    return this.isSameAuthSession(session, this.captureAuthSession());
  }

  private getHeaders(includeAuth = true) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && typeof window !== 'undefined') {
      const token = getValidAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const url = `${API_URL}${endpoint}`;
      // Собираем headers один раз, чтобы можно было корректно удалять Content-Type для FormData
      const extraHeaders: Record<string, string> = {};
      if (options.headers) {
        if (options.headers instanceof Headers) {
          options.headers.forEach((value, key) => {
            extraHeaders[key] = value;
          });
        } else if (Array.isArray(options.headers)) {
          for (const [key, value] of options.headers) {
            extraHeaders[key] = value;
          }
        } else {
          Object.assign(extraHeaders, options.headers as Record<string, string>);
        }
      }

      const hasManualAuthorization = Object.keys(extraHeaders).some((k) => k.toLowerCase() === 'authorization');
      if (!hasManualAuthorization && typeof window !== 'undefined') {
        const access = getAccessToken();
        if (access && isJwtExpired(access)) {
          // Комментарий на русском: стараемся обновить токен до запроса, чтобы не ловить пачку 401 в фоне.
          const session = this.captureAuthSession();
          const refreshed = await this.refreshToken();
          if (!refreshed && this.isCurrentAuthSession(session)) clearAuth();
        }
      }

      const mergedHeaders: Record<string, string> = {
        ...this.getHeaders(),
        ...extraHeaders,
      };

      // ВАЖНО: для FormData нельзя принудительно ставить Content-Type: application/json
      // иначе браузер не добавит boundary, и PHP не увидит файл в $_FILES.
      const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
      if (isFormData) {
        delete mergedHeaders['Content-Type'];
      }

      const response = await fetch(url, {
        ...options,
        headers: mergedHeaders,
      });

      if (!response.ok) {
        let errorDetails: unknown;
        if (response.status === 401 && typeof window !== 'undefined') {
          // Не пытаемся refresh для админских endpoint'ов - там 401 может быть нормальным ответом
          const isAdminEndpoint = endpoint.includes('/admin/') || endpoint.includes('admin-access-check');
          
          if (!isAdminEndpoint) {
            // Токен недействителен, пробуем обновить только для обычных endpoints
            const session = this.captureAuthSession();
            const refreshed = await this.refreshToken();
            if (refreshed || !this.isCurrentAuthSession(session)) {
              return this.request(endpoint, options);
            }
            clearAuth();
            throw new ApiError('Unauthorized', 401, { message: 'Токен истек и не удалось обновить' });
          }
          // Для админских endpoint'ов просто выбрасываем ошибку без refresh
        }
        
        const errorText = await response.text();
        const isExpectedProjectComments404 =
          response.status === 404 && /^\/api\/projects\/\d+\/comments\/?$/.test(endpoint);
        if (!isExpectedProjectComments404) {
          log('error', 'api', 'API request failed', {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
        }
        
        let errorMessage = `API Error: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorDetails = errorData;
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Если не удалось распарсить JSON, используем текст ошибки
          if (errorText) {
            errorMessage = errorText;
            errorDetails = errorText;
          }
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      // Проверяем, есть ли содержимое в ответе
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (!text.trim()) {
          // Пустой ответ
          return {} as T;
        }
        try {
          return JSON.parse(text);
        } catch (error) {
          log('error', 'api', 'JSON parse error', {
            error: error instanceof Error ? error.message : String(error),
            responseText: text,
          });
          throw new Error(`JSON parse error - ${error}`);
        }
      } else {
        // Не JSON ответ, возвращаем как текст
        const text = await response.text();
        return { message: text } as T;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error or unexpected response');
    }
  }

  // === CHAT: Direct messages and conversations ===
  async getConversations() {
    // Возвращает список собеседников с последним сообщением и количеством непрочитанных
    return this.request<{ user: User; last_message: { id: number; message: string; created_at: string; is_read: boolean; from_user: User; to_user: User }; unread_count: number }[]>('/api/chat/conversations/');
  }

  async getDirectMessages(withUserId: number) {
    // Получить историю сообщений с пользователем
    const data: any = await this.request<any>(`/api/chat/messages/${withUserId}/`);
    if (Array.isArray(data)) return data as Array<{ id: number; from_user: User; to_user: User; message: string; created_at: string; is_read: boolean }>;
    if (Array.isArray(data?.messages)) return data.messages as Array<{ id: number; from_user: User; to_user: User; message: string; created_at: string; is_read: boolean }>;
    return [];
  }

  async sendDirectMessage(toUserId: number, message: string) {
    // Отправить сообщение через HTTP fallback
    return this.request<{ id: number; from_user: User; to_user: User; message: string; created_at: string; is_read: boolean }>(
      '/api/chat/direct-messages/',
      { method: 'POST', body: JSON.stringify({ to_user_id: toUserId, message }) }
    );
  }

  async updateOnlineStatus(isOnline: boolean) {
    // Обновить онлайн-статус текущего пользователя
    return this.request<{ user: any; is_online: boolean; last_seen: string; last_activity: string }>(
      '/api/chat/status/online/',
      { method: 'POST', body: JSON.stringify({ is_online: isOnline }) }
    );
  }

  // Один одновременный refresh — устраняет гонку (несколько 401 → оба дергают refresh, второй получает 401 по старому токену и делает clearAuth).
  private refreshPromise: {
    session: ReturnType<ApiClient['captureAuthSession']>;
    promise: Promise<boolean>;
  } | null = null;

  async refreshToken(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    const session = this.captureAuthSession();
    if (!session.storage || !session.refresh) return false;
    if (this.refreshPromise && this.isSameAuthSession(this.refreshPromise.session, session)) {
      return this.refreshPromise.promise;
    }

    const promise = this.doRefreshToken(session);
    this.refreshPromise = { session, promise };
    try {
      return await promise;
    } finally {
      if (this.refreshPromise?.promise === promise) this.refreshPromise = null;
    }
  }

  private async doRefreshToken(session: ReturnType<ApiClient['captureAuthSession']>): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: session.refresh }),
      });

      if (response.ok) {
        const data = await response.json();
        // Смена storage или токенов означает, что ответ принадлежит уже не текущей сессии.
        if (!this.isCurrentAuthSession(session)) return true;
        session.storage.setItem('access', data.access);
        if (data.refresh) {
          session.storage.setItem('refresh', data.refresh);
        }
        return true;
      }
    } catch (error) {
      if (!this.isCurrentAuthSession(session)) return true;
      log('error', 'api.auth', 'Token refresh failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Устаревший отказ не должен очищать или блокировать новую сессию вызывающей стороны.
    return !this.isCurrentAuthSession(session);
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.request<{ access: string; refresh: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

    async getLegacyLoginStatus() {
    return this.request<{
      legacy_login_enabled: boolean;
      email_registration_disabled: boolean;
      social_auth_enabled: boolean;
      social_providers?: { google: boolean; vk: boolean; telegram: boolean; max: boolean };
    }>('/api/auth/legacy-login-enabled');
  }

  async startSocialAuth(provider: 'google' | 'vk' | 'max', intent: 'login' | 'link' = 'login', returnTo: string = '/') {
    return this.request<{ provider: string; intent: string; auth_url: string; expires_in: number }>(
      `/api/auth/social/${provider}/start`,
      {
        method: 'POST',
        body: JSON.stringify({ intent, return_to: returnTo }),
      }
    );
  }

  async verifyTelegramAuth(
    payload: Record<string, unknown>,
    intent: 'login' | 'link' = 'login',
    returnTo: string = '/'
  ) {
    return this.request<{
      provider: string;
      exchange_token?: string;
      return_to: string;
      merge_required?: boolean;
      merge_token?: string;
      merge_preview?: AccountMergePreview;
    }>(
      '/api/auth/social/telegram/verify',
      {
        method: 'POST',
        body: JSON.stringify({ intent, return_to: returnTo, payload }),
      }
    );
  }

  async exchangeSocialAuth(
    exchangeToken: string,
    linkExisting?: { username: string; password: string }
  ) {
    return this.request<{
      access: string;
      refresh: string;
      user: User;
      provider?: string;
      return_to?: string;
      linked_to_existing?: boolean;
    }>(
      '/api/auth/social/exchange',
      {
        method: 'POST',
        body: JSON.stringify(
          linkExisting
            ? { exchange_token: exchangeToken, link_existing: linkExisting }
            : { exchange_token: exchangeToken }
        ),
      }
    );
  }

  async getSocialIdentities() {
    return this.request<{
      identities: Array<{
        provider: 'google' | 'vk' | 'telegram' | 'max';
        linked_at: string;
        username?: string | null;
        email?: string | null;
      }>;
      has_local_password: boolean;
    }>('/api/auth/social/identities');
  }

  async unlinkSocialIdentity(provider: 'google' | 'vk' | 'telegram' | 'max') {
    return this.request<{ ok: boolean; provider: 'google' | 'vk' | 'telegram' | 'max' }>(`/api/auth/social/${provider}`, {
      method: 'DELETE',
    });
  }

  // Регистрация: шаг 1 — отправить код на email
  async requestRegisterCode(email: string) {
    return this.request<{ ok: boolean; token: string; expires_in: number }>('/api/auth/register-request-code/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async register(userData: {
    email: string;
    token: string;
    code: string;
    password: string;
    password_confirm?: string;
  }) {
    // Комментарий на русском: шаг 2 — подтверждаем код и создаём аккаунт; пароль по почте не отправляется.
    return this.request<{ message: string; username?: string }>('/api/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getCurrentUser(options?: { includeContentStats?: boolean }) {
    const query = options?.includeContentStats ? '?include_content_stats=1' : '';
    return this.request<User>(`/api/auth/me${query}`);
  }

  async getUserPreview(userId: number): Promise<UserPreview> {
    return this.request<UserPreview>(`/api/users/preview/${userId}/`);
  }

  private async requestBlob(
    endpoint: string,
    headers: Record<string, string> = {},
    retryAfterRefresh = true
  ): Promise<Blob> {
    const response = await fetch(`${API_URL}${endpoint}`, { headers: { ...this.getHeaders(), ...headers } });
    if (response.status === 401 && retryAfterRefresh) {
      const session = this.captureAuthSession();
      const refreshed = await this.refreshToken();
      if (refreshed || !this.isCurrentAuthSession(session)) {
        return this.requestBlob(endpoint, headers, false);
      }
    }
    if (!response.ok) {
      const errorText = await response.text();
      let message = errorText || `API Error: ${response.status}`;
      try {
        const payload = JSON.parse(errorText) as { error?: string; detail?: string; message?: string };
        message = payload.error || payload.detail || payload.message || message;
      } catch {
        // Ответ не JSON — оставляем серверный текст.
      }
      throw new ApiError(message, response.status, errorText);
    }
    return response.blob();
  }

  async getUserByNickname(nickname: string): Promise<UserPreview> {
    return this.request<UserPreview>(`/api/users/by-nickname/${encodeURIComponent(nickname)}/`);
  }

  async getPublicAppointmentSchedule(ownerId: number) {
    return this.request<{ owner: AppointmentOwner; schedule: AppointmentSchedule }>(`/api/appointments/public/${ownerId}/`);
  }

  async getPublicAppointmentSlots(ownerId: number, from: string, to: string) {
    const query = new URLSearchParams({ from, to });
    return this.request<{ timezone: string; slots: AppointmentSlot[] }>(`/api/appointments/public/${ownerId}/slots/?${query}`);
  }

  async requestAppointmentGuestCode(email: string) {
    return this.request<{ verification_token: string; expires_in: number }>('/api/appointments/guest/code/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async createAppointment(payload: AppointmentCreatePayload) {
    return this.request<{ booking: AppointmentBooking; manage_token: string | null; replayed?: boolean; warnings?: string[] }>('/api/appointments/bookings/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getMyAppointmentBookings() {
    return this.request<{ bookings: AppointmentBooking[] }>('/api/appointments/me/bookings/');
  }

  async getAppointmentBooking(bookingId: number) {
    return this.request<{ booking: AppointmentBooking }>(`/api/appointments/bookings/${bookingId}/`);
  }

  async updateAppointmentBooking(bookingId: number, payload: AppointmentLifecyclePayload) {
    return this.request<{ booking: AppointmentBooking; warnings?: string[] }>(`/api/appointments/bookings/${bookingId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getGuestAppointmentBooking(manageToken: string) {
    return this.request<{ booking: AppointmentBooking }>('/api/appointments/manage/booking/', {
      headers: { 'X-Appointment-Manage-Token': manageToken },
    });
  }

  async updateGuestAppointmentBooking(manageToken: string, payload: AppointmentLifecyclePayload) {
    return this.request<{ booking: AppointmentBooking; warnings?: string[] }>('/api/appointments/manage/booking/', {
      method: 'PATCH',
      headers: { 'X-Appointment-Manage-Token': manageToken },
      body: JSON.stringify(payload),
    });
  }

  async downloadAppointmentCalendar(bookingId: number) {
    return this.requestBlob(`/api/appointments/bookings/${bookingId}/calendar.ics`);
  }

  async downloadGuestAppointmentCalendar(manageToken: string) {
    return this.requestBlob('/api/appointments/manage/calendar.ics', {
      'X-Appointment-Manage-Token': manageToken,
    });
  }

  async getManagedAppointmentSchedules(search = '', scope: 'profile' | 'admin' = 'admin') {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (scope === 'profile') params.set('scope', 'profile');
    const query = params.size ? `?${params}` : '';
    return this.request<{ schedules: AppointmentManagedSchedule[] }>(`/api/appointments/manage/schedules/${query}`);
  }

  async getManagedAppointmentSchedule(ownerId: number) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/`);
  }

  async updateManagedAppointmentSchedule(ownerId: number, payload: AppointmentSchedule & { rules: AppointmentAvailabilityRule[] }) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateManagedAppointmentRules(ownerId: number, rules: AppointmentAvailabilityRule[]) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/rules/`, {
      method: 'PUT',
      body: JSON.stringify({ rules }),
    });
  }

  async createManagedAppointmentException(ownerId: number, exception: AppointmentAvailabilityException & { end_date?: string }) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/exceptions/`, {
      method: 'POST',
      body: JSON.stringify(exception),
    });
  }

  async updateManagedAppointmentException(ownerId: number, exceptionId: number, exception: AppointmentAvailabilityException) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/exceptions/${exceptionId}/`, {
      method: 'PATCH',
      body: JSON.stringify(exception),
    });
  }

  async deleteManagedAppointmentException(ownerId: number, exceptionId: number) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/exceptions/${exceptionId}/`, {
      method: 'DELETE',
    });
  }

  async getManagedAppointmentBookings(ownerId: number) {
    return this.request<{ bookings: AppointmentBooking[] }>(`/api/appointments/manage/schedules/${ownerId}/bookings/`);
  }

  async previewAccountMerge(username: string, password: string) {
    return this.request<AccountMergeRequest>('/api/auth/account-merge/preview', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async inspectAccountMerge(mergeToken: string) {
    return this.request<AccountMergeRequest>('/api/auth/account-merge/inspect', {
      method: 'POST',
      body: JSON.stringify({ merge_token: mergeToken }),
    });
  }

  async confirmAccountMerge(mergeToken: string) {
    return this.request<{
      ok: boolean;
      summary: {
        projects_moved: number;
        memberships_moved: number;
        join_requests_moved: number;
        providers_moved: string[];
        local_credentials_moved: boolean;
      };
    }>('/api/auth/account-merge/confirm', {
      method: 'POST',
      body: JSON.stringify({ merge_token: mergeToken }),
    });
  }

  async verifyMaxAuth(initData: string, state: string) {
    return this.request<{
      provider: 'max';
      exchange_token?: string;
      return_to: string;
      merge_required?: boolean;
      merge_token?: string;
      merge_preview?: AccountMergePreview;
    }>(
      '/api/auth/social/max/verify',
      {
        method: 'POST',
        body: JSON.stringify({ init_data: initData, state }),
      }
    );
  }

  async saveManagedAppointmentTeaching(ownerId: number, rule: AppointmentTeachingInput, ruleId?: number) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/teaching/${ruleId ? `${ruleId}/` : ''}`, {
      method: ruleId ? 'PUT' : 'POST', body: JSON.stringify(rule),
    });
  }

  async deleteManagedAppointmentTeaching(ownerId: number, ruleId: number) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/teaching/${ruleId}/`, { method: 'DELETE' });
  }

  async getManagedAppointmentCalendar(ownerId: number, from: string, to: string) {
    return this.request<AppointmentCalendarPayload>(`/api/appointments/manage/schedules/${ownerId}/calendar/?${new URLSearchParams({ from, to })}`);
  }

  async setAppointmentVisitorRestriction(ownerId: number, bookingId: number, expiresAt: string | null, reason: string) {
    return this.request<{ restriction: AppointmentVisitorRestriction | null }>(`/api/appointments/manage/schedules/${ownerId}/bookings/${bookingId}/restriction/`, {
      method: 'PUT', body: JSON.stringify({ expires_at: expiresAt, reason }),
    });
  }

  async updateManagedAppointmentBooking(ownerId: number, bookingId: number, payload: AppointmentManagerLifecyclePayload) {
    return this.request<{ booking: AppointmentBooking; warnings?: string[] }>(`/api/appointments/manage/schedules/${ownerId}/bookings/${bookingId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async updateManagedAppointmentDelegates(ownerId: number, delegateUserIds: number[]) {
    return this.request<AppointmentManagementPayload>(`/api/appointments/manage/schedules/${ownerId}/delegates/`, {
      method: 'PUT',
      body: JSON.stringify({ delegate_user_ids: delegateUserIds }),
    });
  }

  async searchManagedAppointmentDelegates(ownerId: number, search: string) {
    const query = new URLSearchParams({ search: search.trim() });
    return this.request<{ users: AppointmentDelegate[] }>(`/api/appointments/manage/schedules/${ownerId}/delegates/?${query}`);
  }

  // Проверить доступ к админ панели
  async checkAdminAccess(): Promise<{
    has_access: boolean;
    user: User;
    permissions: string[];
    admin?: { scope: string; allowed_tabs: string[] };
  }> {
    return this.request('/api/auth/admin-access-check');
  }

  // Привязать текущую сессию аналитики к пользователю (после логина)
  async identifyVisitSession(sessionId: string): Promise<{ ok: boolean }> {
    return this.request('/api/analytics/identify', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  // Трекинг визита пользователя (сессия/заход на сайт)
  async trackVisit(payload?: { session_id?: string; path?: string }): Promise<{ ok: boolean; visits?: any }> {
    const body: Record<string, unknown> = { ...(payload || {}) };
    if (!body.path && typeof window !== 'undefined') body.path = window.location.pathname || '/';
    if (!body.session_id && typeof document !== 'undefined') {
      const sid = document.cookie.match(/(?:^|;\s*)sid=([^;]+)/)?.[1];
      if (sid) body.session_id = decodeURIComponent(sid);
    }
    return this.request('/api/auth/track-visit/', { method: 'POST', body: JSON.stringify(body) });
  }

  // Смена пароля
  async changePassword(oldPassword: string, newPassword: string, newPasswordConfirm: string): Promise<{ message: string; detail: string }> {
    return this.request('/api/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm
      }),
    });
  }

  // Обновление профиля
  async updatePreferredSiteTheme(site_theme: SiteThemeId): Promise<{ site_theme: SiteThemeId }> {
    return this.request('/api/auth/profile/update/', { method: 'PATCH', body: JSON.stringify({ site_theme }) });
  }

  async updateProfile(profileData: Partial<User>): Promise<{ message: string; user: User }> {
    return this.request('/api/auth/profile/update/', {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });
  }

  // === Проекты ===
  async getProjects(): Promise<Project[]> {
    return this.request('/api/projects/');
  }

  // Публичные проекты (для гостей/главной страницы)
  async getPublicProjects(limit: number = 8): Promise<Project[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('limit', String(Math.max(1, Math.min(24, limit))));
    return this.request(`/api/projects/public/?${searchParams.toString()}`);
  }

  async getPublicProfileProjects(userId: number, limit: number = 8, offset: number = 0, projectId?: number): Promise<Project[]> {
    if (!Number.isSafeInteger(userId) || userId < 1 || userId > 2147483647
      || !Number.isInteger(limit) || limit < 1 || limit > 24
      || !Number.isSafeInteger(offset) || offset < 0 || offset > 2147483647
      || (projectId !== undefined && (!Number.isSafeInteger(projectId) || projectId < 1 || projectId > 2147483647))) {
      throw new RangeError('Некорректные параметры публичных проектов профиля.');
    }
    const searchParams = new URLSearchParams({
      public_user_id: String(userId),
      limit: String(limit),
      offset: String(offset),
    });
    if (projectId !== undefined) searchParams.set('project_id', String(projectId));
    return this.request(`/api/projects/public/?${searchParams.toString()}`);
  }

  // Публичные проекты обычных пользователей (отдельная витрина/виджет)
  async getPublicUserProjects(limit: number = 8): Promise<Project[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('limit', String(Math.max(1, Math.min(24, limit))));
    return this.request(`/api/projects/public/users/?${searchParams.toString()}`);
  }

  async getPublicProject(projectId: number): Promise<Project> {
    return this.request(`/api/projects/public/${projectId}/`);
  }

  async getMyProjects(): Promise<Project[]> {
    return this.request('/api/projects/my/');
  }

  async getProject(projectId: number): Promise<Project> {
    return this.request(`/api/projects/${projectId}/`);
  }

  // Лайк проекта (cookie-based, с привязкой к user_id если авторизован)
  async likeProject(projectId: number): Promise<{ status: string; message: string; likes_count?: number; user_reaction?: string }> {
    return this.request(`/api/projects/${projectId}/like/`, {
      method: 'POST',
      credentials: 'include',
    });
  }

  // Комментарии проекта
  async getProjectComments(projectId: number): Promise<Comment[]> {
    return this.request(`/api/projects/${projectId}/comments/`);
  }

  async createProjectComment(
    projectId: number,
    content: string,
    parentId?: number | null,
    blockId?: string | null
  ): Promise<{ ok: boolean; id?: number }> {
    return this.request(`/api/projects/${projectId}/comments/`, {
      method: 'POST',
      body: JSON.stringify({ content, parent_id: parentId ?? null, block_id: blockId ?? null }),
    });
  }

  async updateProject(projectId: number, patch: Partial<Project>): Promise<Project> {
    return this.request(`/api/projects/${projectId}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  // Персональная раскладка панелей редактора проекта (UI layout)
  async getProjectEditorLayout(projectId: number): Promise<{ layout: any | null }> {
    return this.request(`/api/projects/${projectId}/editor_layout/`);
  }

  async saveProjectEditorLayout(projectId: number, layout: any | null): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/editor_layout/`, {
      method: 'PUT',
      body: JSON.stringify({ layout }),
    });
  }

  async createProject(projectData: { name: string; description?: string; directions?: ProjectDirection[]; is_public?: boolean; organization_id?: number | null }): Promise<Project> {
    return this.request('/api/projects/', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  // === Организации ===
  async getOrganizations(params?: {
    type?: OrganizationType;
    parent_id?: number | null | 'null';
    search?: string;
    active_only?: boolean;
    flat?: boolean;
  }): Promise<{ success: boolean; data: Organization[]; flat: boolean }> {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.parent_id !== undefined) {
      searchParams.set('parent_id', params.parent_id === null ? 'null' : String(params.parent_id));
    }
    if (params?.search) searchParams.set('search', params.search);
    if (params?.active_only !== undefined) searchParams.set('active_only', params.active_only ? '1' : '0');
    if (params?.flat !== undefined) searchParams.set('flat', params.flat ? '1' : '0');
    const qs = searchParams.toString();
    return this.request(`/api/organizations/${qs ? '?' + qs : ''}`);
  }

  async getOrganization(id: number): Promise<{ success: boolean; data: Organization }> {
    return this.request(`/api/organizations/${id}/`);
  }

  async getOrganizationTypes(): Promise<{ success: boolean; data: OrganizationTypeOption[] }> {
    return this.request('/api/organizations/types/');
  }

  async createOrganization(data: {
    name: string;
    short_name?: string;
    type: OrganizationType;
    parent_id?: number | null;
    description?: string;
    cover_url?: string;
    website?: string;
    address?: string;
    color?: string;
    icon?: string;
    sort_order?: number;
  }): Promise<{ success: boolean; id: number; message: string }> {
    return this.request('/api/organizations/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(id: number, data: Partial<Organization>): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/organizations/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ id, ...data }),
    });
  }

  async deleteOrganization(id: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/organizations/${id}/`, { method: 'DELETE' });
  }

  async getOrganizationsStats(): Promise<{ success: boolean; data: { total: number; by_type: Record<string, number>; projects_with_org: number; projects_without_org: number } }> {
    return this.request('/api/organizations/stats/');
  }

  async joinProject(projectId: number, message?: string): Promise<{ ok: boolean; status?: string; join_request_id?: number }> {
    return this.request(`/api/projects/${projectId}/join/`, { method: 'POST', body: JSON.stringify({ message: message || '' }) });
  }

  async leaveProject(projectId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/leave/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async getProjectMembers(projectId: number): Promise<ProjectMember[]> {
    return this.request(`/api/projects/${projectId}/members/`);
  }

  async updateProjectMember(
    projectId: number,
    payload: { user_id?: number; username?: string; access_level?: number; position?: string | null }
  ): Promise<{ ok: boolean; member?: ProjectMember }> {
    return this.request(`/api/projects/${projectId}/members/update/`, { method: 'POST', body: JSON.stringify(payload || {}) });
  }

  async removeProjectMember(projectId: number, payload: { user_id?: number; username?: string }): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/members/remove/`, { method: 'POST', body: JSON.stringify(payload || {}) });
  }

  async deleteProject(projectId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/`, { method: 'DELETE' });
  }

  async getMyProjectInvites(): Promise<ProjectInvite[]> {
    return this.request('/api/projects/invites/');
  }

  async acceptProjectInvite(inviteId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/invites/${inviteId}/accept/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async declineProjectInvite(inviteId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/invites/${inviteId}/decline/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async inviteToProject(projectId: number, payload: { username: string; message?: string }): Promise<{ ok: boolean; invite?: any }> {
    return this.request(`/api/projects/${projectId}/invite/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getProjectInvites(projectId: number): Promise<ProjectInviteAdminRow[]> {
    return this.request(`/api/projects/${projectId}/invites/`);
  }

  async revokeProjectInvite(inviteId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/invites/${inviteId}/revoke/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async getProjectJoinRequests(params?: { status?: string; project_id?: number }): Promise<ProjectJoinRequestOwnerRow[]> {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.project_id) sp.set('project_id', String(params.project_id));
    const qs = sp.toString();
    return this.request(`/api/projects/join_requests/${qs ? `?${qs}` : ''}`);
  }

  async getMyProjectJoinRequests(params?: { status?: string }): Promise<ProjectJoinRequestMyRow[]> {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    const qs = sp.toString();
    return this.request(`/api/projects/join_requests/my/${qs ? `?${qs}` : ''}`);
  }

  async acceptProjectJoinRequest(joinRequestId: number, payload: { access_level: number } = { access_level: 2 }): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/join_requests/${joinRequestId}/accept/`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async declineProjectJoinRequest(joinRequestId: number, payload: { comment?: string } = {}): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/join_requests/${joinRequestId}/decline/`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async getProjectLinkRequests(params?: { project_id?: number; status?: string }): Promise<ProjectLinkRequestOwnerRow[]> {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.project_id) sp.set('project_id', String(params.project_id));
    const qs = sp.toString();
    return this.request(`/api/projects/project_link_requests/${qs ? `?${qs}` : ''}`);
  }

  async getMyProjectLinkRequests(params?: { status?: string }): Promise<ProjectLinkRequestOwnerRow[]> {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    const qs = sp.toString();
    return this.request(`/api/projects/project_link_requests/my/${qs ? `?${qs}` : ''}`);
  }

  async acceptProjectLinkRequest(requestId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/project_link_requests/${requestId}/accept/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async declineProjectLinkRequest(requestId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/project_link_requests/${requestId}/decline/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async addProjectMaterialsBlocks(projectId: number, blocks: any[]): Promise<{ ok: boolean; status?: string; proposal_id?: number; project?: Project }> {
    return this.request(`/api/projects/${projectId}/materials_blocks/add/`, {
      method: 'POST',
      body: JSON.stringify({ blocks: Array.isArray(blocks) ? blocks : [] }),
    });
  }

  async getProjectMaterialsBlocksProposals(projectId: number): Promise<ProjectMaterialsBlocksProposalRow[]> {
    return this.request(`/api/projects/${projectId}/materials_blocks/proposals/`);
  }

  async approveProjectMaterialsBlocksProposal(projectId: number, proposalId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/materials_blocks/proposals/${proposalId}/approve/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async declineProjectMaterialsBlocksProposal(projectId: number, proposalId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/materials_blocks/proposals/${proposalId}/decline/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async updateProjectMaterialsBlock(projectId: number, block: any): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/materials_blocks/block/update/`, { method: 'POST', body: JSON.stringify({ block }) });
  }

  // ============================================================
  // Блоки проекта (кубики) — новая архитектура
  // ============================================================

  async getProjectPlan(projectId: number, kind: 'current' | 'template' = 'current'): Promise<ProjectPlanResponse> {
    return this.request(`/api/projects/${projectId}/plan/?kind=${kind}`);
  }

  async importProjectPlan(
    projectId: number,
    data: Extract<ProjectPlanImportRequest, { mode: 'preview' }>,
  ): Promise<ProjectPlanImportPreviewResponse>;
  async importProjectPlan(
    projectId: number,
    data: Extract<ProjectPlanImportRequest, { mode: 'commit' }>,
  ): Promise<ProjectPlanImportCommitResponse>;
  async importProjectPlan(
    projectId: number,
    data: ProjectPlanImportRequest,
  ): Promise<ProjectPlanImportPreviewResponse | ProjectPlanImportCommitResponse> {
    return this.request(`/api/projects/${projectId}/plan/import/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProjectBlocks(projectId: number): Promise<ProjectBlock[]> {
    return this.request(`/api/projects/${projectId}/blocks/`);
  }

  async getProjectPresentation(projectId: number): Promise<ProjectPresentation> {
    return this.request(`/api/projects/${projectId}/presentation/`);
  }

  async createProjectBlock(
    projectId: number,
    data: {
      type?: ProjectBlockType;
      title: string;
      content?: string;
      importance?: number;
      implementation_level?: number;
      position?: number;
      tags?: string[];
      meta?: Record<string, any>;
      input_data_types?: DataFlowType[];
      output_data_types?: DataFlowType[];
      expression?: Record<string, any>;
      status?: 'draft' | 'published';
    }
  ): Promise<{ ok: boolean; block: ProjectBlock }> {
    return this.request(`/api/projects/${projectId}/blocks/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadProjectAsset(projectId: number, file: File): Promise<{ url: string; filename: string; mime_type?: string; size_bytes?: number }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/api/projects/${projectId}/assets/upload/`, {
      method: 'POST',
      body: formData,
    });
  }

  // === Файловый менеджер проекта ===
  
  // Список файлов проекта
  async getProjectFiles(projectId: number, folder?: string, type?: string): Promise<ProjectFilesListResponse> {
    const params = new URLSearchParams();
    if (folder) params.set('folder', folder);
    if (type) params.set('type', type);
    const qs = params.toString();
    return this.request(`/api/projects/${projectId}/files/${qs ? `?${qs}` : ''}`);
  }

  // Загрузка файла в проект
  async uploadProjectFile(projectId: number, file: File, folder?: string): Promise<ProjectFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    return this.request(`/api/projects/${projectId}/files/upload/`, {
      method: 'POST',
      body: formData,
    });
  }

  // Информация о файле проекта
  async getProjectFileInfo(projectId: number, fileId: string): Promise<ProjectFileItem> {
    return this.request(`/api/projects/${projectId}/files/${fileId}/info/`);
  }

  // Удаление файла проекта
  async deleteProjectFile(projectId: number, fileId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/projects/${projectId}/files/${fileId}/delete/`, { method: 'DELETE' });
  }

  // Создание папки в проекте
  async createProjectFolder(projectId: number, name: string, parent?: string): Promise<ProjectFolderCreateResponse> {
    return this.request(`/api/projects/${projectId}/files/folders/`, {
      method: 'POST',
      body: JSON.stringify({ name, parent: parent || '' }),
    });
  }

  // Удаление папки проекта
  async deleteProjectFolder(projectId: number, path: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/projects/${projectId}/files/folders/delete/`, {
      method: 'POST',
      body: JSON.stringify({ path }),
    });
  }

  // Информация о хранилище проекта
  async getProjectStorageInfo(projectId: number): Promise<ProjectStorageInfo> {
    return this.request(`/api/projects/${projectId}/files/storage/`);
  }

  async getProjectBlock(projectId: number, blockUid: string): Promise<ProjectBlock> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/`);
  }

  async getProjectBlockAtTime(projectId: number, blockUid: string, time: string): Promise<ProjectBlock> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/at/?time=${encodeURIComponent(time)}`);
  }

  async getProjectBlockVersions(projectId: number, blockUid: string, withContent = false): Promise<ProjectBlockVersion[]> {
    const qs = withContent ? '?with_content=1' : '';
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/versions/${qs}`);
  }

  async updateProjectBlock(
    projectId: number,
    blockUid: string,
    data: Partial<{
      title: string;
      content: string;
      importance: number;
      implementation_level: number;
      position: number;
      tags: string[];
      meta: Record<string, any>;
      input_data_types: DataFlowType[];
      output_data_types: DataFlowType[];
      expression: Record<string, any>;
      change_summary: string;
      expected_updated_at: string;
    }>
  ): Promise<{ ok: boolean; version: number }> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getProjectCollaboration(projectId: number, editingBlockUid: string | null): Promise<ProjectCollaborationState> {
    return this.request(`/api/projects/${projectId}/collaboration/`, {
      method: 'POST',
      body: JSON.stringify({ editing_block_uid: editingBlockUid }),
    });
  }

  async deleteProjectBlock(projectId: number, blockUid: string): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/`, { method: 'DELETE' });
  }

  async publishProjectBlock(projectId: number, blockUid: string): Promise<{ ok: boolean; status: string }> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/publish/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async approveProjectBlock(projectId: number, blockUid: string): Promise<{ ok: boolean; status: string }> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/approve/`, { method: 'POST', body: JSON.stringify({}) });
  }

  async rejectProjectBlock(projectId: number, blockUid: string, comment?: string): Promise<{ ok: boolean; status: string }> {
    return this.request(`/api/projects/${projectId}/blocks/${blockUid}/reject/`, { method: 'POST', body: JSON.stringify({ comment: comment || '' }) });
  }

  // Связи (граф)
  async getProjectEdges(projectId: number): Promise<ProjectEdge[]> {
    return this.request(`/api/projects/${projectId}/edges/`);
  }

  async getProjectGraphValidation(projectId: number): Promise<GraphValidationResult> {
    return this.request(`/api/projects/${projectId}/graph/validate/`);
  }

  async createProjectEdge(
    projectId: number,
    data: { from_uid: string; to_uid: string; relation_type?: ProjectEdgeType; data_type?: DataFlowType; label?: string; weight?: number }
  ): Promise<{ ok: boolean; edge_id: number; data_type?: DataFlowType }> {
    return this.request(`/api/projects/${projectId}/edges/`, { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteProjectEdge(projectId: number, edgeId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/edges/${edgeId}/`, { method: 'DELETE' });
  }

  // Машина времени
  async getProjectTimeline(projectId: number, limit?: number, offset?: number): Promise<ProjectEvent[]> {
    const sp = new URLSearchParams();
    if (limit) sp.set('limit', String(limit));
    if (offset) sp.set('offset', String(offset));
    const qs = sp.toString();
    return this.request(`/api/projects/${projectId}/timeline/${qs ? `?${qs}` : ''}`);
  }

  async getProjectState(
    projectId: number,
    time: string
  ): Promise<{ time: string; blocks: ProjectBlock[]; edges: ProjectEdge[]; events_applied: number }> {
    return this.request(`/api/projects/${projectId}/state/?time=${encodeURIComponent(time)}`);
  }

  async getProjectDiff(
    projectId: number,
    t1: string,
    t2: string
  ): Promise<ProjectDiffResponse> {
    return this.request(`/api/projects/${projectId}/diff/?t1=${encodeURIComponent(t1)}&t2=${encodeURIComponent(t2)}`);
  }

  async getProjectDocs(projectId: number): Promise<ProjectDocs> {
    return this.request(`/api/projects/${projectId}/docs/`);
  }

  // Очередь на одобрение
  async getProjectApprovals(projectId: number): Promise<ProjectBlockApproval[]> {
    return this.request(`/api/projects/${projectId}/approvals/`);
  }

  // Справочники типов
  async getBlockTypes(): Promise<BlockTypeInfo[]> {
    return this.request(`/api/projects/0/block_types/`);
  }

  async getEdgeTypes(): Promise<EdgeTypeInfo[]> {
    return this.request(`/api/projects/0/edge_types/`);
  }

  // ============================================================
  // Переменные проекта
  // ============================================================

  async getProjectVariables(projectId: number): Promise<ProjectVariable[]> {
    return this.request(`/api/projects/${projectId}/variables/`);
  }

  async createProjectVariable(
    projectId: number,
    data: { name: string; var_type: ProjectVariableType; default_value?: string; description?: string }
  ): Promise<{ ok: boolean; variable: ProjectVariable }> {
    return this.request(`/api/projects/${projectId}/variables/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProjectVariable(
    projectId: number,
    variableId: number,
    data: { current_value?: string; default_value?: string; description?: string }
  ): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/variables/${variableId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectVariable(projectId: number, variableId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/variables/${variableId}/`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // Overview (компиляция и экспорт)
  // ============================================================

  async getProjectOverview(
    projectId: number,
    options?: { at?: string; mode?: 'presentation' | 'document' }
  ): Promise<OverviewAST> {
    const params = new URLSearchParams();
    if (options?.at) params.set('at', options.at);
    if (options?.mode) params.set('mode', options.mode);
    const qs = params.toString();
    return this.request(`/api/projects/${projectId}/overview/${qs ? '?' + qs : ''}`);
  }

  async buildProjectOverview(
    projectId: number,
    data: { at?: string; mode?: 'presentation' | 'document'; include_drafts?: boolean }
  ): Promise<{ graph_hash: string; ast: OverviewAST; errors: any[]; warnings: any[]; build_time_ms: number }> {
    return this.request(`/api/projects/${projectId}/overview/build/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async exportProjectOverview(
    projectId: number,
    format: 'md' | 'pdf',
    options?: { at?: string }
  ): Promise<{ content: string; filename: string }> {
    const params = new URLSearchParams({ format });
    if (options?.at) params.set('at', options.at);
    return this.request(`/api/projects/${projectId}/overview/export/?${params.toString()}`);
  }

  // ============================================================
  // Шаблоны и темы Overview
  // ============================================================

  async getOverviewTemplates(): Promise<OverviewTemplate[]> {
    return this.request(`/api/overview/templates/`);
  }

  async getThemePacks(): Promise<ThemePack[]> {
    return this.request(`/api/overview/themes/`);
  }

  async updateProjectTheme(
    projectId: number,
    data: { overview_theme_id?: string; theme_overrides?: Record<string, any> }
  ): Promise<{ ok: boolean }> {
    return this.request(`/api/projects/${projectId}/theme/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // Review Workflow
  // ============================================================

  async getBlockReviews(
    projectId: number,
    options?: { scope?: 'node' | 'pipeline' | 'build'; status?: string; target_id?: string }
  ): Promise<BlockReview[]> {
    const params = new URLSearchParams();
    if (options?.scope) params.set('scope', options.scope);
    if (options?.status) params.set('status', options.status);
    if (options?.target_id) params.set('target_id', options.target_id);
    const qs = params.toString();
    return this.request(`/api/projects/${projectId}/reviews/${qs ? '?' + qs : ''}`);
  }

  async createBlockReview(
    projectId: number,
    data: { scope: 'node' | 'pipeline' | 'build'; target_id: string; title?: string; assignee_id?: number }
  ): Promise<{ ok: boolean; review: BlockReview }> {
    return this.request(`/api/projects/${projectId}/reviews/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReviewComments(reviewId: number): Promise<ReviewComment[]> {
    return this.request(`/api/reviews/${reviewId}/comments/`);
  }

  async addReviewComment(
    reviewId: number,
    data: { comment_type: 'comment' | 'suggestion' | 'issue'; content: string; block_id?: number; line_start?: number; line_end?: number }
  ): Promise<{ ok: boolean; comment: ReviewComment }> {
    return this.request(`/api/reviews/${reviewId}/comments/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async reviewAction(
    reviewId: number,
    action: 'approve' | 'request_changes' | 'reopen' | 'close'
  ): Promise<{ ok: boolean; status: string }> {
    return this.request(`/api/reviews/${reviewId}/action/`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  async resolveReviewComment(reviewId: number, commentId: number): Promise<{ ok: boolean }> {
    return this.request(`/api/reviews/${reviewId}/comments/${commentId}/resolve/`, {
      method: 'POST',
    });
  }

  // ============================================================
  // Universal Block API (Block Type Definitions)
  // ============================================================

  async getBlockTypeDefinitions(): Promise<BlockTypeDefinition[]> {
    return this.request(`/api/block-types/`);
  }

  async getBlockTypeDefinition(typeId: string): Promise<BlockTypeDefinition> {
    return this.request(`/api/block-types/${encodeURIComponent(typeId)}/`);
  }

  async createBlockTypeDefinition(
    data: Omit<BlockTypeDefinition, 'id' | 'is_builtin'>
  ): Promise<{ ok: boolean; block_type: BlockTypeDefinition }> {
    return this.request(`/api/block-types/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBlockTypeDefinition(
    typeId: string,
    data: Partial<BlockTypeDefinition>
  ): Promise<{ ok: boolean }> {
    return this.request(`/api/block-types/${encodeURIComponent(typeId)}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Загрузка аватарки
  async uploadAvatar(avatarFile: File): Promise<{ message: string; avatar_url: string; user: User }> {
    const formData = new FormData();
    formData.append('avatar', avatarFile);

    return this.request('/api/auth/profile/avatar/', {
      method: 'POST',
      body: formData,
    });
  }

  // Загрузка видео для анимированной аватарки
  async uploadAvatarVideo(
    avatarFile: File,
    opts: { startSec: number; durationSec: number; format?: 'gif' | 'webp' }
  ): Promise<{ message: string; avatar_url: string; user: User }> {
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    formData.append('mode', 'video');
    formData.append('start_sec', String(opts.startSec ?? 0));
    formData.append('duration_sec', String(opts.durationSec ?? 2));
    if (opts.format) formData.append('format', opts.format);

    return this.request('/api/auth/profile/avatar/', {
      method: 'POST',
      body: formData,
    });
  }

  // Удаление аватарки
  async deleteAvatar(): Promise<{ message: string }> {
    return this.request('/api/auth/profile/avatar/', {
      method: 'DELETE',
    });
  }

  // News endpoints - оптимизированные для пагинации
  async getNews(params?: { 
    limit?: number; 
    offset?: number; 
    ordering?: string; 
    category?: string; 
    tag?: string; 
    search?: string;
    page?: number;
    author?: string;
  }): Promise<NewsItem[] | { results: NewsItem[]; count: number; next: string | null; previous: string | null }> {
    try {
      const searchParams = new URLSearchParams();
      
      // Поддержка пагинации по страницам и offset/limit
      if (params?.page) {
        searchParams.set('page', params.page.toString());
      } else {
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.offset) searchParams.set('offset', params.offset.toString());
      }
      
      if (params?.ordering) searchParams.set('ordering', params.ordering);
      if (params?.category) searchParams.set('category', params.category);
      if (params?.tag) searchParams.set('tag', params.tag);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.author) searchParams.set('author', params.author);

      // Публичный API - не требует авторизации, оптимизирован для быстрой загрузки
      const url = `${API_URL}/api/news/?${searchParams.toString()}`;
      
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        // Отключаем кеширование для актуальных данных
        cache: 'no-cache'
      });

      if (!response.ok) {
        const errorText = await response.text();
        log('error', 'api.news', 'Ошибка API новостей', {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Обрабатываем как пагинированный ответ (DRF возвращает объект) или массив
      if (data.results) {
        // Пагинированный ответ от DRF
        return {
          results: Array.isArray(data.results) ? (data.results as any[]).map(normalizeNewsItem) : [],
          count: data.count,
          next: data.next,
          previous: data.previous
        };
      } else if (Array.isArray(data)) {
        // Прямой массив (fallback для старых версий API)
        return (data as any[]).map(normalizeNewsItem) as NewsItem[];
      } else {
        // Неожиданный формат, возвращаем пустой массив
        log('warn', 'api.news', 'Неожиданный формат ответа API новостей', { data });
        return [];
      }
    } catch (error) {
      log('error', 'api.news', 'Ошибка при загрузке новостей', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  // Поиск по опубликованным новостям (для привязки к проектам/резюме)
  async searchNews(query: string, limit: number = 10): Promise<NewsSearchItem[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const searchParams = new URLSearchParams();
    searchParams.set('q', q);
    searchParams.set('limit', String(Math.max(1, Math.min(25, limit))));
    return this.request(`/api/news/search?${searchParams.toString()}`);
  }

  // Поиск пользователей в базе (directory). Важно: UI должен решать, кому это показывать.
  async searchUsersDirectory(query: string, limit: number = 10): Promise<UserDirectoryItem[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const searchParams = new URLSearchParams();
    searchParams.set('search', q);
    searchParams.set('is_active', 'true');
    searchParams.set('limit', String(Math.max(1, Math.min(20, limit))));
    // /api/users/search -> php_api/users.php (handleList)
    const data: any = await this.request(`/api/users/search?${searchParams.toString()}`);
    const arr = Array.isArray(data) ? data : (data?.results || data?.items || []);
    return Array.isArray(arr) ? (arr as UserDirectoryItem[]) : [];
  }

  async getNewsItem(slug: string): Promise<NewsItem> {
    try {
      // Публичный API - не требует авторизации, оптимизирован для быстрой загрузки
      // Комментарий на русском: slug — часть пути, поэтому всегда кодируем (защита от пробелов/кириллицы/служебных символов).
      const safeSlug = encodeURIComponent(String(slug || '').trim());
      const url = `${API_URL}/api/news/${safeSlug}/`;
      
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Комментарий на русском: если пользователь авторизован — добавляем Bearer, чтобы бэкенд мог отдать user_reaction.
      if (typeof window !== 'undefined') {
        const token = getAccessToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        headers,
        // Комментарий на русском: нужен cookie like_session_id для user_reaction (лайки) в local_test.
        credentials: 'include',
        // Добавляем кеширование на стороне браузера
        cache: 'default'
      });

      if (!response.ok) {
        const errorText = await response.text();
        log('error', 'api.news', 'Ошибка API одиночной новости', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          slug: safeSlug,
        });
        
        if (response.status === 404) {
          throw new Error('Новость не найдена');
        }
        
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return normalizeNewsItem(data);
    } catch (error) {
      log('error', 'api.news', 'Ошибка при загрузке новости', {
        error: error instanceof Error ? error.message : String(error),
        slug,
      });
      throw error;
    }
  }

  // News CRUD endpoints (public)
  async createPublicNews(newsData: any) {
    return this.request<NewsItem>('/api/news/create/', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  async updatePublicNews(slug: string, newsData: any) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<NewsItem>(`/api/news/${safeSlug}/edit/`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
  }

  async deletePublicNews(slug: string) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request(`/api/news/${safeSlug}/delete/`, {
      method: 'DELETE',
    });
  }

  // Comments endpoints
  // Публичные комментарии работают по slug страницы
  async getNewsComments(slug: string) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<Comment[]>(`/api/news/${safeSlug}/comments/`);
  }

  async createNewsComment(slug: string, content: string, parentId?: number) {
    // Бэкенд ожидает поле parent (pk), а не parent_id
    const payload: any = { content };
    if (typeof parentId === 'number') payload.parent = parentId;
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<Comment>(`/api/news/${safeSlug}/comments/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Реакции на комментарии (лайк/сердечко)
  async setNewsCommentReaction(slug: string, commentId: number, reactionType: 'like' | 'heart') {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: 'set' | 'removed'; user_reaction: 'like' | 'heart' | null; upvotes: number; downvotes: number }>(
      `/api/news/${safeSlug}/comments/${commentId}/reaction/`,
      { method: 'POST', body: JSON.stringify({ reaction_type: reactionType }) }
    );
  }

  // Скрыть/показать комментарий (для админов)
  async toggleNewsCommentHidden(slug: string, commentId: number) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: 'visible' | 'hidden' }>(`/api/news/${safeSlug}/comments/${commentId}/hide/`, { method: 'PATCH' });
  }

  // Полное удаление комментария (для админов)
  async deleteNewsComment(slug: string, commentId: number) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: 'deleted' }>(`/api/news/${safeSlug}/comments/${commentId}/delete/`, { method: 'DELETE' });
  }

  // Реакции на новость (лайк и пр.)
  async setNewsReaction(slug: string, reactionType: 'like' | 'dislike' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry') {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: 'created' | 'updated' }>(`/api/news/${safeSlug}/reaction/`, {
      method: 'POST',
      body: JSON.stringify({ reaction_type: reactionType }),
    });
  }

  async removeNewsReaction(slug: string) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: 'deleted' }>(`/api/news/${safeSlug}/reaction/`, {
      method: 'DELETE',
    });
  }

  // Анонимные лайки для новостей
  async addAnonymousLike(slug: string) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    return this.request<{ status: string; message: string; likes_count?: number; user_reaction?: string }>(`/api/news/${safeSlug}/like/`, {
      method: 'POST',
      credentials: 'include', // Включаем куки в запрос
    });
  }

  // Друзья, которым понравилась новость (до 10)
  async getNewsLikedFriends(slug: string, limit: number = 10) {
    const safeSlug = encodeURIComponent(String(slug || '').trim());
    const n = Math.max(1, Math.min(10, Number(limit) || 10));
    const sp = new URLSearchParams();
    sp.set('limit', String(n));
    return this.request<UserPreview[]>(`/api/news/${safeSlug}/likes/friends/?${sp.toString()}`);
  }

  // Admin User Management endpoints
  async getUsers(params?: { search?: string; role?: string; is_active?: boolean; limit?: number; offset?: number }): Promise<NormalizedListResponse<User> & { stats?: AdminUsersStats }> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.role) searchParams.set('role', params.role);
    if (params?.is_active !== undefined) searchParams.set('is_active', params.is_active.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/api/auth/admin/users/?${queryString}`
      : '/api/auth/admin/users/';

    const response = await this.request<RawListResponse<User> & { stats?: AdminUsersStats }>(endpoint);
    return { ...normalizeListResponse(response), stats: response && !Array.isArray(response) ? response.stats : undefined };
  }

  async createUser(userData: {
    username?: string;
    email: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    role?: string;
    role_ids?: number[];
    auto_generate_password?: boolean;
  }) {
    return this.request<{
      message: string;
      user: User;
      password_generated: boolean;
      generated_password?: string;
    }>('/api/auth/admin/users/create/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: number, userData: Partial<User>) {
    return this.request<User>(`/api/auth/admin/users/${userId}/`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  }

  async getUserDetail(userId: number) {
    return this.request<User>(`/api/auth/admin/users/${userId}/`);
  }

  async deleteUser(userId: number) {
    return this.request<{ message: string }>(`/api/auth/admin/users/${userId}/`, {
      method: 'DELETE',
    });
  }

  async toggleUserStatus(userId: number) {
    return this.request<{ message: string; is_active: boolean }>(`/api/auth/admin/users/${userId}/toggle-status/`, {
      method: 'POST',
    });
  }

  async setUserRoles(userId: number, roleIds: number[]) {
    return this.request<{
      message: string;
      user_id: number;
      old_role: string;
      new_role: string;
      roles: UserRole[];
      role: UserRole | null;
      old_roles?: UserRole[];
      new_roles?: UserRole[];
      requires_logout: boolean;
    }>(`/api/auth/admin/users/${userId}/change-role/`, {
      method: 'POST',
      body: JSON.stringify({ role_ids: roleIds }),
    });
  }

  // Совместимость: старая установка одной роли по имени
  async changeUserRole(userId: number, roleName: string) {
    return this.request<{ message: string; user_id: number; old_role: string; new_role: string; roles: UserRole[]; role: UserRole | null; requires_logout: boolean }>(`/api/auth/admin/users/${userId}/change-role/`, {
      method: 'POST',
      body: JSON.stringify({ role_name: roleName }),
    });
  }

  async bulkCreateUsers(users: any[], csvFile?: File, options?: {
    autoGeneratePasswords?: boolean;
    autoGenerateUsernames?: boolean;
    exportCsv?: boolean;
  }) {
    const formData = new FormData();
    
    if (csvFile) {
      formData.append('csv_file', csvFile);
    } else {
      formData.append('users', JSON.stringify(users));
    }

    // Добавляем опции
    if (options?.autoGeneratePasswords !== undefined) {
      formData.append('auto_generate_passwords', options.autoGeneratePasswords.toString());
    }
    if (options?.autoGenerateUsernames !== undefined) {
      formData.append('auto_generate_usernames', options.autoGenerateUsernames.toString());
    }
    if (options?.exportCsv !== undefined) {
      formData.append('export_csv', options.exportCsv.toString());
    }

    // Комментарий на русском: используем общий request (он сам корректно работает с FormData).
    return this.request<{
      created_count: number;
      error_count: number;
      created_users: any[];
      errors: any[];
      has_generated_passwords: boolean;
      export_available?: boolean;
      csv_data?: string;
    }>('/api/auth/admin/users/bulk-create/', {
      method: 'POST',
      body: formData,
    });
  }

  async getRoles(): Promise<NormalizedListResponse<AdminRole>> {
    const response = await this.request<RawListResponse<AdminRole>>('/api/auth/admin/roles/');
    return normalizeListResponse(response);
  }

  async getUsersStats() {
    return this.request<{
      total_users: number;
      active_users: number;
      inactive_users: number;
      roles_stats: any[];
      recent_users: User[];
    }>('/api/auth/admin/users/stats/');
  }

  // Генерация пароля
  async generatePassword(length: number = 12, includeSymbols: boolean = true) {
    return this.request<{
      password: string;
      length: number;
      has_symbols: boolean;
    }>('/api/auth/admin/generate-password/', {
      method: 'POST',
      body: JSON.stringify({
        length: length,
        include_symbols: includeSymbols
      }),
    });
  }

  // Экспорт CSV файла с созданными пользователями
  async exportCreatedUsersCSV(): Promise<Blob> {
    const token = getAccessToken();
    if (!token) throw new Error('Нет токена авторизации');
    const response = await fetch(`${API_URL}/api/auth/admin/users/export-csv/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Ошибка экспорта файла');
    }

    return response.blob();
  }

  // Admin dashboard stats
  async getAdminDashboardStats() {
    return this.request<{
      totalUsers: number;
      totalNews: number;
      totalComments: number;
      pendingNews: number;
      totalProjects?: number;
      activeVacancies?: number;
      totalApplications?: number;
      onlineUsers?: number;
    }>('/api/auth/admin/dashboard/stats');
  }

  // Метрики сервера (диск/CPU/RAM) для дашборда
  async getAdminServerStats(): Promise<AdminServerStats> {
    return this.request<AdminServerStats>('/api/auth/admin/server/stats/');
  }

  // Admin visits stats (аналитика посещений)
  async getAdminVisitsStats() {
    return this.request<{
      totals: { day: number; month: number; all: number; day_unique: number; month_unique: number; all_unique: number };
      unauth: { day: number; month: number; all: number };
      // Комментарий на русском: распределение устройств (mobile/tablet/desktop). Может отсутствовать, если бэкенд ещё не обновлён.
      devices?: {
        day: { mobile: number; tablet: number; desktop: number; unknown: number };
        month: { mobile: number; tablet: number; desktop: number; unknown: number };
        all: { mobile: number; tablet: number; desktop: number; unknown: number };
      };
      recent_unauth: Array<{ ip: string; path: string; time: string; country?: string | null; country_code?: string | null }>;
    }>('/api/auth/admin/analytics/visits-stats');
  }

  // Admin visits log (журнал визитов)
  async getAdminVisitsLog(params?: {
    auth?: 'all' | 'auth' | 'unauth' | '0' | '1';
    limit?: number;
    offset?: number;
    q?: string;
    includeInternal?: boolean;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.auth && params.auth !== 'all') searchParams.set('auth', params.auth);
    if (typeof params?.limit === 'number') searchParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') searchParams.set('offset', String(params.offset));
    if (params?.q) searchParams.set('q', params.q);
    if (params?.includeInternal) searchParams.set('include_internal', '1');
    const qs = searchParams.toString();

    return this.request<{
      count: number;
      next: string | null;
      previous: string | null;
      results: Array<{
        id: number;
        ip: string;
        country?: string | null;
        country_code?: string | null;
        path: string;
        time: string;
        is_authenticated: boolean;
        user: null | { id: number; username: string; display_name?: string | null };
      }>;
    }>(`/api/auth/admin/analytics/visits-log${qs ? `?${qs}` : ''}`);
  }

  // Admin recent activities
  async getAdminRecentActivities() {
    return this.request<{
      activities: Array<{
        action: string;
        user: string;
        time: string;
        type: string;
        details: string;
      }>;
    }>('/api/auth/admin/recent-activities');
  }


  // Admin News endpoints
  async getAdminNews() {
    return this.request<NewsItem[]>('/api/news/admin/');
  }

  async getAdminNewsItem(id: number) {
    return this.request<NewsItem>(`/api/news/admin/${id}/`);
  }

  async createNews(newsData: {
    title: string;
    teaser: string;
    content: string;
    tags?: string;
    category_id?: number;
    status?: 'draft' | 'published';
    cover_image?: string;
    content_blocks?: any;
    meta_title?: string;
    meta_description?: string;
    page_atmosphere?: any;
    project_ids?: number[];
  }) {
    log('info', 'api.news.admin', 'Creating news', { newsData });
    
    // Преобразуем теги из строки в массив
    const processedData: any = {
      title: newsData.title,
      teaser: newsData.teaser,
      content: newsData.content,
      status: newsData.status || 'draft',
      tags: newsData.tags ? newsData.tags.split(',').map(tag => tag.trim()) : []
    };

    // Добавляем опциональные поля
    if (newsData.cover_image) {
      processedData.cover_image = newsData.cover_image;
    }
    if (newsData.content_blocks) {
      processedData.content_blocks = newsData.content_blocks;
    }
    if (newsData.category_id) {
      processedData.category_id = newsData.category_id;
    }
    if (newsData.meta_title) {
      processedData.meta_title = newsData.meta_title;
    }
    if (newsData.meta_description) {
      processedData.meta_description = newsData.meta_description;
    }
    if (newsData.page_atmosphere) {
      processedData.page_atmosphere = newsData.page_atmosphere;
    }
    if (Array.isArray(newsData.project_ids)) {
      processedData.project_ids = newsData.project_ids;
    }

    log('debug', 'api.news.admin', 'Prepared news payload', { processedData });
    log('debug', 'api.news.admin', 'Submitting news payload', {
      url: `${API_URL}/api/news/admin/`,
    });

    try {
      const result = await this.request<NewsItem>('/api/news/admin/', {
        method: 'POST',
        body: JSON.stringify(processedData),
      });
      log('info', 'api.news.admin', 'News created successfully', { id: result.id, slug: result.slug });
      return result;
    } catch (error) {
      log('error', 'api.news.admin', 'Failed to create news', {
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  async updateNews(id: number, newsData: Partial<NewsItem>) {
    return this.request<NewsItem>(`/api/news/admin/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(newsData),
    });
  }

  async deleteNews(id: number) {
    return this.request<{ message: string }>(`/api/news/admin/${id}/`, {
      method: 'DELETE',
    });
  }

  async publishNews(id: number) {
    return this.request<{ message: string }>(`/api/news/admin/${id}/publish/`, {
      method: 'POST',
    });
  }

  async unpublishNews(id: number) {
    return this.request<{ message: string }>(`/api/news/admin/${id}/unpublish/`, {
      method: 'POST',
    });
  }

  async getNewsStats() {
    return this.request<{
      stats: {
        total: number;
        published: number;
        draft: number;
        archived: number;
        total_views: number;
      };
      popular_news: NewsItem[];
      recent_news: NewsItem[];
    }>('/api/news/admin/stats/');
  }

  async uploadMedia(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<{ url: string; filename: string }>('/api/admin/media/', {
      method: 'POST',
      body: formData,
    });
  }

  // Быстрый флаг обновлений динамических данных
  async getUpdates(): Promise<UpdatesSnapshot> {
    return this.request('/api/updates/', { cache: 'no-store' });
  }

  // === НАСТРОЙКИ САЙТА ===

  // Получить настройки сайта (публичный API)
  async getSiteSettings(): Promise<SiteSettings> {
    const response = await fetch(`${API_URL}/api/settings/`);
    if (!response.ok) {
      throw new Error('Ошибка загрузки настроек сайта');
    }
    return response.json();
  }

  // Получить настройки сайта (админ API)
  async getAdminSiteSettings(): Promise<SiteSettings> {
    return this.request('/api/settings/admin/');
  }

  // Обновить настройки сайта (админ API)
  async updateSiteTheme(theme_id: SiteThemeId, expected_revision: number, overrides: ThemeOverrides): Promise<SiteSettings> {
    return this.request('/api/settings/admin/', {
      method: 'PUT', body: JSON.stringify({ site_theme_update: { theme_id, expected_revision, overrides } }),
    });
  }

  async updateSiteBackground(expected_revision: number, config: BackgroundConfig): Promise<SiteSettings> {
    return this.request('/api/settings/admin/', {
      method: 'PUT', body: JSON.stringify({ site_background_update: { expected_revision, config } }),
    });
  }

  async updateSiteSettings(settings: Partial<SiteSettings>): Promise<{ message: string; data: SiteSettings }> {
    log('info', 'api.settings.admin', 'Обновляем настройки сайта', { settings });
    
    const response = await this.request<SiteSettings>('/api/settings/admin/', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    
    return {
      message: 'Настройки успешно сохранены',
      data: response
    };
  }

  // === Вакансии (админ) ===
  async getAdminVacancies(): Promise<any[]> {
    return this.request('/api/vacancies/admin/vacancies/', { method: 'GET' });
  }

  async createAdminVacancy(payload: any): Promise<any> {
    return this.request('/api/vacancies/admin/vacancies/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateAdminVacancy(id: number, payload: any): Promise<any> {
    return this.request(`/api/vacancies/admin/vacancies/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteAdminVacancy(id: number): Promise<void> {
    await this.request(`/api/vacancies/admin/vacancies/${id}/`, {
      method: 'DELETE',
    });
  }

  async getVacancyApplications(): Promise<any[]> {
    return this.request('/api/vacancies/admin/applications/', { method: 'GET' });
  }
}

export const apiClient = new ApiClient();
